import {format} from 'date-fns'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  idFromUserEvent,
  ObjectType,
  stickersContainerId,
  StorageDepartmentHierarchy,
  UserEvent
} from 'yougile-model'
import {Sync} from '@client/sync'
import {noPropagate} from '@containers/hooks'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {Table} from '../common/table/table'
import {dateWithTimeFormat} from '../stats-timeline-store'
import {ActionIcon} from '../utils/action-icons/action-icons'
import {getActionName} from '../utils/action-name'
import {ObjectName} from '../utils/object-name'
import {click, getHint, getProjectTitle} from '../utils/utils'
import {FilesPreview} from './files-preview'
import {StatsTimelineRowUsername} from './username'

interface Props {
  user: UserProxy
  time: number
  event: UserEvent
  restore: (objId: DataObjectId, objType: ObjectType) => void
  hasError: boolean
  hierarchy: StorageDepartmentHierarchy
}

const TwRestore = tw.div`
  outline-none
  cursor-pointer
  text-action-default
  text-12
  hover:underline
  self-center
`

const TwUserCell = tw.div`
  mx-6
  text-12
`

const TwHintCell = tw.div`
  mx-6
  text-12
  truncate
`

const TwTitleCell = tw.div`
  mx-6
  text-12
  truncate
`

const TwActionCell = tw.div`
  text-12
  flex
  justify-between
  min-w-0
`

// eslint-disable-next-line max-lines-per-function
export const StatisticsRow = (props: Props) => {
  const {user, event, time, restore, hasError, hierarchy} = props

  const box = Sync.getCurrentSandbox()
  const obj = box.getHub(idFromUserEvent(event.obj))
  if (!obj) {
    return null
  }
  const objType = box.getType(obj)
  const clickRestore = (e: React.MouseEvent) => {
    noPropagate(e)
    restore(obj?.id, objType)
  }
  if (hasError) {
    return null
  }
  return (
    <Table.Row onClick={click(event)} cursorPointer={objType === 'task'}>
      <Table.Cell>
        <div className="mx-6 text-11">{format(time, dateWithTimeFormat)}</div>
      </Table.Cell>
      <Table.Cell>
        <TwActionCell>
          <div className="flex min-w-0 truncate">
            <div className="mr-8">
              <ActionIcon event={event} />
            </div>
            <div
              className={`flex min-w-0 ${
                event.type === 'file' ? 'flex-col' : ''
              }`}
            >
              <div className="flex flex-wrap items-center min-w-0">
                <div className="mr-4 overflow-hidden text-ellipsis">
                  {getActionName({
                    event,
                    objType: objType,
                    isSticker: obj.location === stickersContainerId,
                    obj
                  })}
                </div>
                <ObjectName event={event} obj={obj} />
              </div>
              <FilesPreview event={event} />
            </div>
          </div>
          {event.type === 'delete' && (
            <>
              {obj.deleted && (
                <TwRestore onClick={clickRestore}>{Lang.msg.restore}</TwRestore>
              )}
            </>
          )}
        </TwActionCell>
      </Table.Cell>
      <Table.Cell>
        <TwUserCell>
          <StatsTimelineRowUsername user={user} />
        </TwUserCell>
      </Table.Cell>
      <Table.Cell>
        <TwHintCell
          title={getHint({event, obj, withoutLimit: true}, hierarchy)}
        >
          {getHint({event, obj}, hierarchy)}
        </TwHintCell>
      </Table.Cell>
      <Table.Cell>
        <TwTitleCell
          title={objType === 'task' ? getProjectTitle(event) : undefined}
        >
          {objType === 'task' && getProjectTitle(event)}
        </TwTitleCell>
      </Table.Cell>
    </Table.Row>
  )
}
